<template>
    <rkm-modal
        class="rkm-modal"
        large
        ref="RkmModal"
        title="Receita Médica"
    >
        <List
            v-if="isList"
            :data="data"
            @delete="data.model.receitaMedica.isAssinada = false"
            @edit="openForm"
        />

        <Form
            v-if="isForm"
            :data="data"
            @saved="saved"
            ref="RkmForm"
            style="margin-top: -1rem; margin-bottom: -1rem;"
        />

        <template v-slot:footer="{ cancel }">
            <template v-if="isList">
                <rkm-button
                    :disabled="!!loading"
                    @click="cancel"
                >
                    Fechar
                </rkm-button>

                <rkm-button
                    :disabled="!!loading || data.model.receitaMedica.medicamentos.length === 0"
                    @click="print()"
                >
                    Imprimir
                </rkm-button>

                <rkm-button
                    :disabled="!!loading"
                    @click="openForm(null)"
                    color="primary"
                >
                    Novo medicamento
                </rkm-button>
            </template>

            <template v-if="isForm">
                <rkm-button
                    :disabled="!!loading"
                    @click="layout = 'list'"
                >
                    Voltar
                </rkm-button>

                <rkm-button
                    :disabled="!!loading"
                    @click="$refs.RkmForm.save()"
                    color="primary"
                >
                    Salvar
                </rkm-button>
            </template>
        </template>

        <ModalAssinaturaEletronica
            :ref="modalAssinaturaEletronicaRefName"
            @sign="onSign"
            @skip-sign="onSkipSign"
            download-without-asking
            open-instead-of-download
            v-bind="modalAssinaturaEletronicaReceitaMedicaProps"
        />
    </rkm-modal>
</template>

<script>
import Form from './Form'
import List from './List'

// Enums.
import tipoReceita from '@/enums/cadastro/medicamento/tipoReceita'
import viaAdministracao from '@/enums/cadastro/medicamento/viaAdministracao'
import tipoAplicacao from '@/enums/pronto-atendimento/receita/tipoAplicacao'
import tipoPosologia from '@/enums/pronto-atendimento/receita/tipoPosologia'

// Mixins.
import { repository, error, stringCase, object, file, prontoAtendimentoAssinaturaEletronica } from '@/mixins'

// Vuex.
import { mapGetters, mapActions } from 'vuex'

export default {
    components: { Form, List },
    computed: {
        ...mapGetters({ loading: 'getLoading' }),
        isForm() {

            return this.layout == 'form'
        },
        isList() {

            return this.layout == 'list'
        },
    },
    created() {

        this.repositories = {
            impressao: this.getConectaSusRepository('impressao.prontoAtendimento.atendimentoMedico')
        }
    },
    data: function() {

        return {
            layout: 'list',
            enums: { tipoReceita, viaAdministracao, tipoAplicacao, tipoPosologia },
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        openForm(item) {

            this.layout = 'form'

            this.$nextTick().then(() => {

                this.$refs.RkmForm.load(item)
            })

        },
        print() {

            if (this.data.model.receitaMedica.isAssinada) {

                this.data.model.receitasMedicasAssinadas.slice(-1)[0].forEach(r => this.openPdfBase64(r))

                return
            }

            let promises = []
            let fileNames = []

            const setPrinting = assinar => {

                promises = []
                fileNames = []

                const data = {
                    paraAssinaturaEletronica: assinar,
                    pacienteId: this.data.form.paciente.id,
                    receitas: [{
                        medicamentos: this.data.model.receitaMedica.medicamentos
                            .map(item => {

                                let data = {
                                    medicamentoId: item.medicamento.id,
                                }

                                if (item.viaAdministracao.id === this.enums.viaAdministracao.OUTRAS) {

                                    data.viaAdministracaoOutras = item.viaAdministracaoOutras
                                }

                                switch (item.tipoAplicacao.id) {

                                    case this.enums.tipoAplicacao.DOSE_UNICA: {

                                        return {
                                            ...data,
                                            quantidade: item.quantidade,
                                            recomendacoes: item.recomendacoes,
                                        }
                                    }
                                    case this.enums.tipoAplicacao.USO_CONTINUO: {

                                        data = {
                                            ...data,
                                            quantidade: item.quantidade,
                                            recomendacoes: item.recomendacoes,
                                        }

                                        return item.tipoPosologia === this.enums.tipoPosologia.FREQUENCIA
                                            ? {
                                                ...data,
                                                intervaloTempo: item.intervaloTempo,
                                                intervaloMedidaTempoId: item.intervaloMedidaTempo.id,
                                            }
                                            : {
                                                ...data,
                                                turnoId: item.turno.id
                                            }
                                    }
                                    case this.enums.tipoAplicacao.LIVRE: {

                                        return {
                                            ...data,
                                            descricao: item.descricao,
                                        }
                                    }
                                    default: {

                                        data = {
                                            ...data,
                                            quantidade: item.quantidade,
                                            duranteTempo: item.duranteQuantidade,
                                            duranteMedidaTempoId: item.duranteMedidaTempo.id,
                                            recomendacoes: item.recomendacoes,
                                        }

                                        return item.tipoPosologia === this.enums.tipoPosologia.FREQUENCIA
                                            ? {
                                                ...data,
                                                intervaloTempo: item.intervaloTempo,
                                                intervaloMedidaTempoId: item.intervaloMedidaTempo.id,
                                            }
                                            : {
                                                ...data,
                                                turnoId: item.turno.id
                                            }
                                    }
                                }
                            })
                    }]
                }

                if (this.data.model.receitaMedica.medicamentos
                    .find(m => m.medicamento.receitaTipoId == this.enums.tipoReceita.COMUM)
                ) {

                    promises.push(() => this.repositories.impressao.receita(
                        null,
                        this.camelObjectKeysToSnakeRecursive(data)
                    ))

                    fileNames.push('receita-medica')
                }

                const tiposEspeciais = [
                    this.enums.tipoReceita.ESPECIAL,
                    this.enums.tipoReceita.ESPECIAL_AMARELA,
                    this.enums.tipoReceita.ESPECIAL_AZUL,
                ]

                if (this.data.model.receitaMedica.medicamentos
                    .find(m => tiposEspeciais.includes(m.medicamento.receitaTipoId))
                ) {

                    promises.push(() => this.repositories.impressao.receitaEspecial(
                        null,
                        this.camelObjectKeysToSnakeRecursive(data)
                    ))

                    fileNames.push('receita-medica-especial')
                }
            }

            const printWithoutSign = () => {

                setPrinting(false)

                this.setLoading()

                Promise.all(promises.map(p => p()))
                    .then(responses => responses.forEach(r => this.openPrinting(r)))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }

            if (this.data.additionals.assinaturaEletronicaAtiva) {

                this.onSign = signedFiles => {

                    this.data.model.receitasMedicasAssinadas.push(signedFiles)
                    this.data.model.receitaMedica.isAssinada = true
                }

                this.onSkipSign = () => printWithoutSign()

                this.askToSignReceita(
                    async () => {

                        setPrinting(true)

                        this.setLoading()

                        Promise.all(promises.map(p => p()))
                            .then(responses => this.signReceitaMedica(
                                responses.map(r => new Blob([r.data], { type: r.headers['content-type'] })),
                                fileNames
                            ))
                            .catch(this.handleError)
                            .finally(() => this.unsetLoading())
                    },
                    async () => printWithoutSign()
                )
            }
            else {

                printWithoutSign()
            }

           /*  this.setLoading()

            Promise.all(promises.map(p => p()))
                .then(responses => {

                    const openFiles = () => responses.forEach(r => this.openPrinting(r))

                    this.onSign = signedFiles => {

                        this.data.model.receitasMedicasAssinadas.push(signedFiles)
                        this.data.model.receitaMedica.isAssinada = true
                    }

                    this.onSkipSign = () => openFiles()

                    if (this.data.additionals.assinaturaEletronicaAtiva) {

                        const files = responses.map(r => new Blob([r.data], { type: r.headers['content-type'] }))

                        this.askToSignReceita(
                            async () => this.signReceitaMedica(files, fileNames),
                            async () => openFiles()
                        )
                    }
                    else {

                        openFiles()
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading()) */
        },
        openPrinting(response) {

            const content = new Blob([response.data], { type: response.headers['content-type'] })
            const url = URL.createObjectURL(content)
            const link = document.createElement('a')

            link.href = url
            link.target = '_blank'
            link.click()

            URL.revokeObjectURL(url);
        },
        saved({ isModelTouched }) {

            if (isModelTouched) {

                this.data.model.receitaMedica.isAssinada = false
            }

            this.layout = 'list'
        },
        open() {

            this.$refs.RkmModal.show()
        },
        close() {

            this.$refs.RkmModal.hide()
        },
    },
    mixins: [repository, error, stringCase, object, file, prontoAtendimentoAssinaturaEletronica],
    props: {
        data: { type: Object, required: true }
    },
}
</script>